import type { Distance, Margins, Point, SpatialRectangle } from "@pdfking/math"
import { ZERO_MARGINS } from "@pdfking/math/constants"
import type { ColorID } from "@pdfking/renderer-api"

import type { ShapeSequence } from "#internal"

import { CreatePath } from "../../../shapes/CreatePath"

/**
 * Mirrors Python `plot_rect_guides` + `plot_rect_guides_args`
 * (strategy.py:489-517). Emits a line per guide coordinate; each
 * x-guide gets a vertical line at `position.x + coord`, each y-guide
 * gets a horizontal line at `position.y + coord`. Lines extend by
 * `padding` on each end.
 *
 * Iteration order matches Python: x guides (vertical lines) first,
 * then y guides (horizontal lines).
 */
export function* PlotRectGuides(
  { x: x1, y: y1, width, height }: SpatialRectangle<Distance>,
  guides: Point<Distance[]>,
  stroke_color: ColorID,
  { top, bottom, left, right }: Margins<Distance> = ZERO_MARGINS
): ShapeSequence {
  // Python iterates ("x", "y", "margin_top", "margin_bottom") first.
  // For axis x: vertical line at x1 + coord, y from (y1 - margin_top) to (y2 + margin_bottom).
  for (const coord of guides.x) {
    const x = x1 + coord
    yield CreatePath(
      [
        { x, y: y1 - top },
        { x, y: y1 + height + bottom },
      ] as Point<Distance>[],
      stroke_color
    )
  }
  // Then ("y", "x", "margin_left", "margin_right"):
  // For axis y: horizontal line at y1 + coord, x from (x1 - margin_left) to (x2 + margin_right).
  for (const coord of guides.y) {
    const y = y1 + coord
    yield CreatePath(
      [
        { x: x1 - left, y },
        { x: x1 + width + right, y },
      ] as Point<Distance>[],
      stroke_color
    )
  }
}
